import { useEffect, useMemo, useState } from "react";

import { useField } from "formik";
import { debounce } from "lodash-es";
import queryString from "query-string";

import { useProviders } from "hooks/tivoApps";

const QUERY_DEBOUNCE_TIME = 250;

type FilterValue = queryString.ParsedQuery<boolean | number | string>[string];

export type useProviderFieldUniqueProps = {
  name: string;

  notUniqueError?: string;
  queryParamName?: string;
};

export type useProviderFieldUniqueResult = {
  error?: string;
  pending: boolean;
  unique?: boolean;
};

export function useProviderFieldUnique({
  name,
  notUniqueError,
  queryParamName,
}: useProviderFieldUniqueProps): useProviderFieldUniqueResult {
  const [, { initialValue, value }] = useField(name);
  const [filterValue, setFilterValue] = useState<FilterValue>(null);
  const filterName = queryParamName ?? name;

  const updateFilterValue = useMemo(
    () => debounce((v: FilterValue) => setFilterValue(v), QUERY_DEBOUNCE_TIME),
    [setFilterValue]
  );

  useEffect(() => {
    updateFilterValue(value);
  }, [value, updateFilterValue]);

  const query = useProviders({ [filterName]: filterValue, limit: 1, page: 1 });

  if (value === initialValue) {
    return { pending: false, unique: true };
  }

  if (["", null, undefined].some((v) => v === filterValue)) {
    return { pending: true };
  }

  if (query.isPending) {
    return { pending: true };
  }

  if (query.isError) {
    return { error: query.error.message, pending: false };
  }

  const unique = query.data.meta?.total === 0;

  return {
    error: unique ? undefined : notUniqueError,
    pending: false,
    unique,
  };
}
